import type { Vector2 } from '@sorrem/shared';
import { SKILL_TREE_DIAL_SIZE } from './skill-tree-layout';
import { polar } from './skill-tree-math';
import { SKILL_TREE_BRANCH_RULES } from './skill-tree-model';

/**
 * Paths and geometry for the skill tree's SVG pieces, generated by
 * `game-content/scripts/generate-skill-tree-pieces.mjs` and served from
 * `game-content/assets/skill-tree/` (see its README). Node pieces share a
 * 120-unit box centered on (60, 60); the numbers here must match the
 * generator.
 */

const PIECE_ROOT = '/assets/skill-tree';

export function toPieceUrl(path: string): string {
    return `${PIECE_ROOT}/${path}`;
}

/** The square box every node and dial piece is drawn in, in piece units. */
export const PIECE_BOX = 120;
/** The middle of the piece box, which rings, arcs, and the spiral wind around. */
export const PIECE_CENTER: Vector2 = { x: PIECE_BOX / 2, y: PIECE_BOX / 2 };
/** Dial pieces are drawn in the piece box, scaled up to the dial's size. */
export const DIAL_PIECE_SCALE = SKILL_TREE_DIAL_SIZE / PIECE_BOX;

/** Degrees between the starts of neighboring Martial ring segments: one segment per level. */
export const MARTIAL_SEGMENT_PITCH = 360 / SKILL_TREE_BRANCH_RULES.martial.maxStep;

/** Martial ring segment `index` (0 at the top) starts at this angle, in degrees clockwise from +x. */
export function resolveMartialSegmentStartAngle(index: number): number {
    return -90 + index * MARTIAL_SEGMENT_PITCH + 5;
}

export const MARTIAL_SEGMENT_SWEEP = 26;

/** Where each Essence level ring sits on the spiral, in degrees clockwise from straight down (ring 1 first). */
export const ESSENCE_RING_ANGLES: readonly number[] = [35, 75, 115, 190, 310];

/** The Essence core's radius, as large as Martial's; the spiral starts at its edge. */
export const ESSENCE_CORE_RADIUS = 34;
const ESSENCE_RING_1_RADIUS = 44;
const ESSENCE_RING_5_RADIUS = 54;

/** The spiral's distance from the node center at `angle` along it: eases from the core edge to ring 1, then widens evenly to ring 5. */
function resolveEssenceSpiralRadius(angle: number): number {
    const slope = (ESSENCE_RING_5_RADIUS - ESSENCE_RING_1_RADIUS) / 275;
    if (angle >= 35) return ESSENCE_RING_1_RADIUS + slope * (angle - 35);
    const u = angle / 35;
    const startSlope = 0.55 * 35;
    const endSlope = slope * 35;
    // Cubic Hermite from (core edge, startSlope) to (ring 1, endSlope).
    return (2 * u ** 3 - 3 * u ** 2 + 1) * ESSENCE_CORE_RADIUS
        + (u ** 3 - 2 * u ** 2 + u) * startSlope
        + (-2 * u ** 3 + 3 * u ** 2) * ESSENCE_RING_1_RADIUS
        + (u ** 3 - u ** 2) * endSlope;
}

/** A point on the Essence spiral, in the node box, `angle` degrees clockwise from straight down. */
export function resolveEssenceSpiralPoint(angle: number): Vector2 {
    return polar(PIECE_CENTER, resolveEssenceSpiralRadius(angle), 90 + angle);
}

/** Essence level ring centers, in the node box (ring 1 first). */
export const ESSENCE_RING_CENTERS: readonly Vector2[] = ESSENCE_RING_ANGLES.map(resolveEssenceSpiralPoint);

/** Radius of the Martial level ring's segments (midway between its outer 50 and inner 43). */
export const MARTIAL_RING_RADIUS = 46.5;
/** Radius of a dial's arc and studs, in its 120-unit box. */
export const DIAL_ARC_RADIUS = 46;

/** The dial arc starts at this angle and sweeps 300 degrees clockwise. */
export const DIAL_START_ANGLE = 120;
export const DIAL_SWEEP = 300;

/** Where a dial stud at `percent` sits, in the dial's 120-unit box. */
export function resolveDialStudPoint(percent: number): Vector2 {
    return polar(PIECE_CENTER, DIAL_ARC_RADIUS, DIAL_START_ANGLE + DIAL_SWEEP * (percent / 100));
}

/** The middle of Martial ring segment `index`, in the node box. */
export function resolveMartialSegmentCenter(index: number): Vector2 {
    return polar(PIECE_CENTER, MARTIAL_RING_RADIUS, resolveMartialSegmentStartAngle(index) + MARTIAL_SEGMENT_SWEEP / 2);
}

/**
 * A pie wedge around `center` from `fromAngle` to `toAngle` (degrees,
 * clockwise from +x), used as a clip path to show part of a filled piece.
 */
export function buildWedgePath(center: Vector2, radius: number, fromAngle: number, toAngle: number): string {
    const sweep = Math.max(0, toAngle - fromAngle);
    if (sweep >= 359.9) {
        return `M${center.x - radius} ${center.y}a${radius} ${radius} 0 1 0 ${radius * 2} 0a${radius} ${radius} 0 1 0 ${-radius * 2} 0Z`;
    }
    const start = polar(center, radius, fromAngle);
    const end = polar(center, radius, toAngle);
    const largeArc = sweep > 180 ? 1 : 0;
    return `M${center.x} ${center.y}L${start.x} ${start.y}A${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}Z`;
}
